export const legalModalContent = {
  privacy: {
    title: 'Privacy Policy',
    body: `**alMATHEMA Education Inc.**

**Effective Date:** January 1, 2026  
**Last Updated:** January 1, 2026

alMATHEMA Education Inc. (“alMATHEMA,” “we,” “us,” “our”) respects your privacy. This Privacy Policy explains what information we collect, how we use it, and the choices you have when you use our website and our diagnostic Services (collectively, the “Services”).

**What information we collect**

We collect personal information only when you voluntarily provide it, such as when you:

1. Create a student or faculty account

2. Complete a diagnostic or submit responses

3. Contact us for support or send feedback

The information we collect may include:

1. Account information (such as name, email address, role, and login credentials)

2. Diagnostic information (such as responses, progress, and diagnostic results)

3. Communications (such as messages you send to us)

We also collect limited non-personal information automatically to help keep the Services secure and improve performance, such as browser type, device information, IP address, and basic usage logs.

**How we use information**

We use information to:

1. Provide the Services, including creating accounts, delivering diagnostics, and generating reports

2. Support faculty pilots by allowing faculty to view results for students in their pilot group

3. Maintain security, prevent misuse, and troubleshoot issues

4. Improve the Services through quality assurance and feature development

5. Communicate with you about service updates, support requests, and important notices

We do not sell personal information.

We do not use student diagnostic information for targeted advertising.

**Faculty pilots and student information**

If a student joins a faculty pilot group, the student’s diagnostic results and related activity within the Services may be visible to the faculty user(s) connected to that pilot group. Faculty are expected to use this information only for legitimate educational support within the pilot context.

**FERPA and student education records**

To the extent that information maintained by alMATHEMA constitutes “education records” under the Family Educational Rights and Privacy Act (FERPA), we support educational institutions in handling such records in a manner consistent with FERPA. When alMATHEMA is used in connection with an educational institution, we process student information solely for legitimate educational purposes as directed by faculty users. Educational institutions remain responsible for determining whether information maintained through the Services constitutes “education records” under FERPA and for complying with their obligations under applicable law. Students and their eligible parents or guardians may exercise rights under FERPA by contacting the relevant educational institution. If you have questions about how FERPA applies to your use of the Services, please contact us at [contact@almathema.com](mailto:contact@almathema.com).

**Service providers**

We may share information with trusted service providers that help us operate the Services, such as hosting, databases, email delivery, and security monitoring. These service providers are permitted to use information only to perform services for alMATHEMA and must protect it.

**Legal requirements**

We may disclose information if required by law or if we believe disclosure is necessary to protect the rights, safety, and security of alMATHEMA, our users, or the public.

**Cookies and analytics**

We may use cookies or similar technologies to:

1. Keep you signed in and support essential site functionality

2. Maintain security and prevent abuse

3. Understand usage patterns so we can improve the Services

You can control cookies through your browser settings. If you block cookies, parts of the Services may not function properly.

**Deidentified and aggregated information**

We may create and use deidentified or aggregated information to understand how the Services are used and to improve the Services. This information is not intended to identify you.

**Account deletion and data removal**

You may request to close your account at any time. Upon request, we will delete or deidentify personal information associated with your account, subject to limited retention necessary for security, fraud prevention, dispute resolution, compliance with legal obligations, and enforcement of agreements. We may retain deidentified or aggregated information.

To request account deletion, contact us at: **contact@almathema.com**

**Children’s privacy**

The Services are intended for users who are 13 years of age or older. We do not knowingly collect personal information from children under 13\. If you are under 13, you may not create an account or use the Services without verified parental or guardian consent provided through your educational institution. If we learn that we have inadvertently collected personal information from a child under 13 without appropriate authorization, we will delete it promptly. If you believe a child under 13 has provided personal information to us, please contact us at contact@almathema.com and we will take appropriate action.

**Security**

We use reasonable safeguards designed to protect personal information during transmission and storage. No method of transmission over the Internet or method of electronic storage is completely secure, so we cannot guarantee absolute security.

**External links**

Our Services may include links to third party websites. We do not control those websites and are not responsible for their privacy practices. Please review the privacy policy of any site you visit.

**Changes to this Privacy Policy**

We may update this Privacy Policy from time to time. Any changes will be posted on this page with an updated “Last Updated” date. Your continued use of the Services after changes are posted means you accept the updated policy.

**Contact**

If you have questions or requests regarding this Privacy Policy, contact:

**alMATHEMA Education Inc.**  
Email: **contact@almathema.com**

`,
  },
  terms: {
    title: 'Terms & Conditions',
    body: `**alMATHEMA Education Inc.**

**Effective Date:** January 1, 2026  
**Last Updated:** January 1, 2026

These Terms of Use (“Terms”) govern access to and use of the websites, applications, diagnostic assessments, dashboards, and related services provided by **alMATHEMA Education Inc.** (“alMATHEMA,” “we,” “us,” “our”) (collectively, the “Services”).

By creating an account, accessing, or using the Services, you agree to these Terms. If you do not agree, do not use the Services.

**1\. Purpose of the Services**

alMATHEMA provides low stakes diagnostic and readiness tools designed to help learners identify specific mathematics skills to strengthen and to help faculty support student learning.

The Services are not a placement decision, grade, or guarantee of academic outcome.

**2\. Who may use the Services**

The Services are intended for:

* **Students** who complete diagnostics for learning support

* **Faculty** who pilot the Services and view results for students in their course or pilot group

You must be at least 13 years old to create an account or use the Services. Users under 13 may only access the Services with verified parental or guardian consent provided through their educational institution. If you are under 18, your parent or guardian should review and agree to these Terms on your behalf where required by applicable law.

**3\. Accounts and access**

**3.1 Account types**

We currently offer:

* Student accounts

* Faculty accounts

**3.2 Account responsibility**

You are responsible for:

* Providing accurate account information

* Maintaining confidentiality of your login credentials

* Activity that occurs under your account

**Account sharing is not permitted.** We may suspend or terminate access if we reasonably believe credentials are being shared or the Services are being misused.

**3.3 Faculty responsibilities in pilots**

Faculty users agree to:

* Use student information only for legitimate educational support within the pilot context

* Avoid sharing student identifiable results publicly or outside the pilot team

* Follow applicable institutional policies and applicable law when using student information

**4\. Privacy**

Our collection and use of personal information is described in our **Privacy Policy** at \[link\]. By using the Services, you acknowledge that you have read the Privacy Policy.

**5\. Intellectual property**

Unless otherwise stated, the Services and all content within them, including assessment items, question text, answer choices, explanations, diagnostic frameworks, skill mappings, scoring logic, reports, and branding, are owned by alMATHEMA or its licensors and are protected by intellectual property laws.

alMATHEMA grants you a limited, nonexclusive, nontransferable, revocable license to access and use the Services for their intended educational purpose.

You may not:

* Copy, reproduce, distribute, publish, sell, sublicense, or publicly display assessment items or reports except as expressly permitted by alMATHEMA in writing

* Create derivative works from assessment content

* Compile assessment items into an item bank

* Remove proprietary notices

* Use our name or branding in a way that suggests endorsement without permission

**6\. Prohibited use**

You agree not to:

* Scrape, crawl, or systematically extract content or data from the Services

* Use bots, scripts, or automated methods to access the Services or collect content

* Reverse engineer, decompile, or attempt to discover source code or underlying models, except where prohibited by law

* Circumvent security controls, rate limits, or access restrictions

* Interfere with the Services’ operation or integrity

* Upload malware or harmful code

* Use the Services to harass, threaten, defame, or violate the rights of others

**7\. Artificial intelligence and model training restriction**

You may not use any part of the Services, including assessment items, prompts, reports, feedback outputs, or other materials, to train, test, or improve any artificial intelligence or machine learning model that is not operated by alMATHEMA.

You may not use the Services or outputs to build or improve competing datasets, item banks, or assessment systems.

**8\. User submissions and learner work**

Students may submit responses and other information through the Services (“User Submissions”).

Students retain ownership of their User Submissions. By submitting User Submissions, you grant alMATHEMA permission to store, process, and analyze them to:

* Provide the Services and generate diagnostic outputs

* Maintain security and prevent misuse

* Improve and refine the Services

We may create and retain **deidentified and aggregated** data that does not reasonably identify a student or faculty user. We may use that deidentified and aggregated data for research, quality assurance, reporting, and service improvement.

**9\. Feedback and suggestions**

If you provide feedback, ideas, or suggestions about the Services, you agree that alMATHEMA may use them without restriction or compensation, including to improve the Services. We will not publish personally identifiable information in connection with feedback without permission.

**10\. Service changes and availability**

We may modify, suspend, or discontinue all or part of the Services at any time. We may also update assessment content, scoring logic, or reporting formats as part of improvement and quality assurance.

We aim to keep Services available but do not guarantee uninterrupted access.

**11\. Disclaimers**

The Services are provided on an “as is” and “as available” basis.

To the fullest extent permitted by law, alMATHEMA disclaims all warranties, express or implied, including implied warranties of merchantability, fitness for a particular purpose, and noninfringement.

**12\. Limitation of liability**

To the fullest extent permitted by law, alMATHEMA will not be liable for indirect, incidental, special, consequential, or punitive damages, including loss of data or academic outcomes, arising from or related to use of the Services.

To the fullest extent permitted by law, alMATHEMA’s total liability for any claim arising out of or related to the Services will not exceed **the greater of**:

* One hundred dollars, or

* The amount paid by you to alMATHEMA for the Services in the twelve months before the event giving rise to the claim

Some jurisdictions do not allow certain limitations, so parts of this section may not apply to you.

**13\. Indemnification**

To the extent permitted by applicable law, you agree to indemnify and hold harmless alMATHEMA and its officers, directors, employees, and contractors from claims, damages, liabilities, and expenses arising out of your intentional misuse of the Services or your material violation of these Terms. This section does not apply to claims arising from alMATHEMA’s own negligence or misconduct.

**14\. Suspension and termination**

You may stop using the Services at any time. You may request account deletion as described in the Privacy Policy.

We may suspend or terminate access if we believe you violated these Terms, if your use creates risk to the Services or other users, or if required by law.

**15\. Governing law**

These Terms are governed by the laws of the State of Florida, without regard to conflict of law principles.

**16\. Changes to these Terms**

We may update these Terms from time to time. We will post the updated version and update the “Last Updated” date. Continued use after the update takes effect means you accept the revised Terms.

**17\. Contact**

Questions about these Terms may be sent to:  
**alMATHEMA Education Inc.**  
Email: contact@almathema.com`,
  },
} as const;
